import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchUsersApi,
  fetchTaxonomiessApi,
  fetchUserByIdApi,
  fetchTaxonomyByIdApi,
  addBoxApi,
  updateboxApi,
} from "../../Api/Box";
import toast from "react-hot-toast";

interface BoxFormProps {
  url: string;
  onClose: () => void;
  boxId?: string;
  userId?: string;
}


export const BoxForm = ({ url, onClose, boxId, userId }: BoxFormProps) => {
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const [taxonomies, setTaxonomies] = useState<any[]>([]);
  const [selectedTaxonomy, setSelectedTaxonomy] = useState("");
  const [selectedTaxonomyValue, setSelectedTaxonomyValue] = useState("");
  const [filteredTaxonomies, setFilteredTaxonomies] = useState<any[]>([]);
  const [showTaxonomyDropdown, setShowTaxonomyDropdown] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  const fetchUsersData = async () => {
    const data = await fetchUsersApi(url);
    if (data) {
      setUsers(data as any[]);
      setFilteredUsers(data as any[]);
    } else {
      console.error("Failed to fetch users");
    }
  };

  // const fetchTaxonomiesData = async () => {
  //   const data = await fetchTaxonomiessApi();
  //   if (data) {
  //     const filteredTaxonomies = data.filter(
  //       (taxonomy: any) => taxonomy.type === "Box"
  //     );
  //     setTaxonomies(filteredTaxonomies);
  //   } else {
  //     console.error("Failed to fetch taxonomies");
  //   }
  // };

  const fetchTaxonomiesData = async () => {
    const data = await fetchTaxonomiessApi(url);
    if (data) {
      const filteredTaxonomies = data.filter(
        (taxonomy: any) => taxonomy.type === "Box"
      );
      setTaxonomies(filteredTaxonomies);
      setFilteredTaxonomies(filteredTaxonomies);
    } else {
      console.error("Failed to fetch taxonomies");
    }
  };

  const prefillFields = async (userId: string, boxId: string) => {
    try {
      const user = await fetchUserByIdApi(userId, url);
      const taxonomy = await fetchTaxonomyByIdApi(boxId, url);

      if (user) {
        setSelectedUser(user.id);
        setSelectedUserName(user.name);
      }
      if (taxonomy) setSelectedTaxonomy(taxonomy.id);
      setSelectedTaxonomyValue(taxonomy.value);

      setLoading(false);
      setEditMode(true);
    } catch (error) {
      console.error("Failed to prefill fields:", error);
    }
  };

  useEffect(() => {
    fetchUsersData();
    fetchTaxonomiesData();

  const state = { boxId, userId };
    if (state?.boxId && state?.userId) {
      prefillFields(state.boxId, state.userId);
      setEditMode(true);
      setLoading(true);
    } else {
      setEditMode(false);
      setLoading(false);
    }
  }, [location.state]);

  const handleUserSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSelectedUserName(query);

    if (query.trim() === "") {
      setShowUserDropdown(false);
      return;
    }

    const matchingUsers = users.filter(
      (user) =>
        user.name && user.name.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredUsers(matchingUsers);
    setShowUserDropdown(matchingUsers.length > 0);
  };

  const handleTaxonomySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSelectedTaxonomyValue(query);

    if (query.trim() === "") {
      setShowTaxonomyDropdown(false);
      return;
    }

    const matchingTaxonomies = taxonomies.filter((taxonomy) =>
      taxonomy.value.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredTaxonomies(matchingTaxonomies);
    setShowTaxonomyDropdown(matchingTaxonomies.length > 0);
  };

  const handleUserInputFocus = () => {
    if (selectedUserName.trim() !== "") {
      const matchingUsers = users.filter((user) =>
        user.name.toLowerCase().includes(selectedUserName.toLowerCase())
      );
      setFilteredUsers(matchingUsers);
      setShowUserDropdown(matchingUsers.length > 0);
    }
  };

  const handleBoxInputFocus = () => {
    if (selectedTaxonomyValue.trim() !== "") {
      const matchingTaxonomies = taxonomies.filter((taxonomy) =>
        taxonomy.value
          .toLowerCase()
          .includes(selectedTaxonomyValue.toLowerCase())
      );
      setFilteredTaxonomies(matchingTaxonomies);
      setShowTaxonomyDropdown(matchingTaxonomies.length > 0);
    }
  };

  const handleUserSelect = (user: any) => {
    setSelectedUser(user.id);
    setSelectedUserName(user.name);
    setShowUserDropdown(false);
  };

  const handleTaxonomySelect = (taxonomy: any) => {
    setSelectedTaxonomy(taxonomy.id);
    setSelectedTaxonomyValue(taxonomy.value);
    setShowTaxonomyDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || !selectedTaxonomy) {
      toast.error("Please fill all the fields", {
        id: "boxError",
      });
    }
    try {
      if (editMode) {
        await updateboxApi(selectedUser, selectedTaxonomy, url);
        toast.success(
          editMode ? "Box updated successfully!" : "Box added successfully!"
        );
        setSelectedUser("");
        setSelectedTaxonomy("");
      } else {
        await addBoxApi(selectedUser, selectedTaxonomy, url);
        toast.success(
          editMode ? "Box updated successfully!" : "Box added successfully!"
        );
        setSelectedUser("");
        setSelectedTaxonomy("");
      }
      navigate("/admin/box");
    } catch (error) {
      console.error(error);
      toast.error(editMode ? "Unable to Edit Box" : "Unable to Add Box", {
        id: "boxError",
      });
    }
  };

  const handleCancelClick = () => {
    setSelectedUser("");
    setSelectedTaxonomy("");
    onClose();
  };

  return (
    <div className="px-4 dark:bg-gray-800">
      <div>
        {loading ? (
          <div className="flex justify-center items-center h-screen">
            <div
              className="flex justify-center items-center h-12 w-12 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] text-blue-600"
              role="status"
            >
              <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                Loading...
              </span>
            </div>
          </div>
        ) : (
          <div>
            <form>
              <h1 className="text-3xl font-bold text-blue-600 capitalize dark:text-white mb-4">
                {editMode ? "Edit Box" : "Add Box"}
              </h1>

              <div className="mb-6 w-full relative">
                <label className="text-gray-900 dark:text-gray-200 font-semibold">
                  User <span className="text-red-500">*</span>
                </label>

                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 mt-2 text-gray-800 bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                  value={selectedUserName}
                  onChange={handleUserSearch}
                  onFocus={handleUserInputFocus}
                  placeholder="Search for a User"
                />

                {showUserDropdown && filteredUsers.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-400 rounded-md shadow-lg dark:bg-gray-800 dark:border-gray-700">
                    {filteredUsers.map((user) => (
                      <li
                        key={user.id}
                        onClick={() => handleUserSelect(user)}
                        className="px-4 py-2 cursor-pointer hover:bg-blue-100 dark:hover:bg-gray-700"
                      >
                        {user.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mb-6 w-full relative">
                <label className="text-gray-900 dark:text-gray-200 font-semibold">
                  Box <span className="text-red-500">*</span>
                </label>

                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 mt-2 text-gray-800 bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                  value={selectedTaxonomyValue}
                  onChange={handleTaxonomySearch}
                  placeholder="Search Box"
                  onFocus={handleBoxInputFocus}
                  disabled={editMode}
                />

                {showTaxonomyDropdown && filteredTaxonomies.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-400 rounded-md shadow-lg dark:bg-gray-800 dark:border-gray-700">
                    {filteredTaxonomies.map((taxonomy) => (
                      <li
                        key={taxonomy.id}
                        onClick={() => handleTaxonomySelect(taxonomy)}
                        className="px-4 py-2 cursor-pointer hover:bg-blue-100 dark:hover:bg-gray-700"
                      >
                        {taxonomy.value}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex space-x-4 mt-6 justify-start">
                <button
                  type="submit"
                  onClick={handleSubmit}
                  className="px-8 py-2.5 leading-5 text-white transition-colors duration-300 transform bg-blue-600 rounded-md hover:bg-blue-500 focus:outline-none focus:bg-gray-600"
                >
                  {editMode ? "Edit" : "Save and Close"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelClick}
                  className="px-8 py-2.5 leading-5 text-white transition-colors duration-300 transform bg-blue-600 rounded-md hover:bg-blue-500 focus:outline-none focus:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
